import { z } from 'zod';

import {
  MiceCorporatePathSchema,
  MiceEventPremisesDraftSchema,
  MiceNamedRefSchema,
  MiceOrganizerSchema,
  MicePremisesKeySchema,
  MiceWorkspaceOptionsSchema,
  refineCorporatePaths,
} from './mice';
import { MICE_EVENT_RULES, MiceEventRuleSchema } from './mice_event_rules';
import { MiceEventPlacesSchema } from './mice_places';
import { NumericIdSchema } from './mice_refs';
export { SelfServiceActionRequestSchema, type SelfServiceActionRequest } from './mice_refs';
const IdSchema = z.string().min(1);

/** Criação do evento pelo organizador do cliente (chamada interna do agente/app). */
export const SelfServiceCreateEventRequestSchema = z
  .object({
    organizer: MiceOrganizerSchema,
    name: z.string().trim().min(1).max(120),
    eventStart: z.iso.date(),
    eventEnd: z.iso.date(),
    debitCompanyId: IdSchema,
    costCenterId: IdSchema.optional(),
    costCenterPath: MiceCorporatePathSchema.default('event'),
    motiveId: IdSchema.optional(),
    motivePath: MiceCorporatePathSchema.default('event'),
    corporateProjectId: IdSchema.optional(),
    corporateProjectPath: MiceCorporatePathSchema.default('event'),
    conversationId: z.string().min(1).max(200).nullable(),
  })
  .refine((body) => body.eventEnd >= body.eventStart, {
    path: ['eventEnd'],
    message: 'O fim do evento não pode ser antes do início',
  })
  .superRefine(refineCorporatePaths);
export type SelfServiceCreateEventRequest = z.infer<typeof SelfServiceCreateEventRequestSchema>;

export const SelfServiceOrganizerQuerySchema = z.object({ organizerId: NumericIdSchema });
export type SelfServiceOrganizerQuery = z.infer<typeof SelfServiceOrganizerQuerySchema>;

/** Lista de eventos do organizador: `archived` escolhe arquivados ou ativos (query string, sempre texto). */
export const SelfServiceEventsQuerySchema = SelfServiceOrganizerQuerySchema.extend({
  archived: z.enum(['true', 'false']).default('false'),
});
export type SelfServiceEventsQuery = z.infer<typeof SelfServiceEventsQuerySchema>;

export const SelfServiceCorporateQuerySchema = z.object({
  clientId: NumericIdSchema,
  debitCompanyId: IdSchema.optional(),
});
export type SelfServiceCorporateQuery = z.infer<typeof SelfServiceCorporateQuerySchema>;

/** `costCenters`/`projects` são nulos quando a consulta veio sem `debitCompanyId`. */
export const SelfServiceCorporateOptionsSchema = MiceWorkspaceOptionsSchema.extend({
  costCenters: z.array(MiceNamedRefSchema).nullable(),
  projects: z.array(MiceNamedRefSchema).nullable(),
  /** O cliente exige projeto e a empresa de débito tem projetos; falso sem `debitCompanyId`. */
  projectRequired: z.boolean().default(false),
});
export type SelfServiceCorporateOptions = z.infer<typeof SelfServiceCorporateOptionsSchema>;

export const SelfServiceDraftExtractRequestSchema = z.object({
  organizerId: NumericIdSchema,
  text: z.string().trim().min(1).max(5000),
  /** Regras que o organizador disse de fato neste texto (as demais seguem pendentes). */
  stated: z.array(MiceEventRuleSchema).max(MICE_EVENT_RULES.length).optional(),
});
export type SelfServiceDraftExtractRequest = z.infer<typeof SelfServiceDraftExtractRequestSchema>;

export const SelfServiceDraftEditRequestSchema = z.object({
  organizerId: NumericIdSchema,
  fields: MiceEventPremisesDraftSchema.refine((fields) => Object.keys(fields).length > 0, {
    message: 'Informe ao menos um campo',
  }),
  /** Regras que a edição da ficha tocou: saem de `rulesPending`. */
  rules: z.array(MiceEventRuleSchema).max(MICE_EVENT_RULES.length).optional(),
});
export type SelfServiceDraftEditRequest = z.infer<typeof SelfServiceDraftEditRequestSchema>;

/** Grava local e hotéis do evento (substitui o bloco todo); vale também com premissas confirmadas.
 *  `base`: os lugares de onde o cliente calculou `places`; diferente dos gravados ⇒ 409
 *  `premises_changed` (o cliente relê e refaz). Obrigatória: a rota nasceu com ela. */
export const SelfServicePlacesRequestSchema = z.object({
  organizerId: NumericIdSchema,
  places: MiceEventPlacesSchema,
  base: MiceEventPlacesSchema,
});
export type SelfServicePlacesRequest = z.infer<typeof SelfServicePlacesRequestSchema>;

/** Nome e/ou datas do evento pelo chat, só enquanto as premissas não estão confirmadas. Com uma
 *  data só, o mice confere a ordem contra a outra, já gravada. */
export const SelfServiceEventDetailsRequestSchema = z
  .object({
    organizerId: NumericIdSchema,
    name: z.string().trim().min(1).max(120).optional(),
    eventStart: z.iso.date().optional(),
    eventEnd: z.iso.date().optional(),
  })
  .refine((body) => Boolean(body.name ?? body.eventStart ?? body.eventEnd), {
    message: 'Informe o nome ou as datas',
  })
  .refine((body) => !body.eventStart || !body.eventEnd || body.eventEnd >= body.eventStart, {
    path: ['eventEnd'],
    message: 'O fim do evento não pode ser antes do início',
  });
export type SelfServiceEventDetailsRequest = z.infer<typeof SelfServiceEventDetailsRequestSchema>;

/**
 * `details.code` dos 409 das rotas do organizador: rascunho mudado durante a operação, compras do
 * evento encerradas (também nas rotas do operador que criam ou enfileiram compra) e evento já
 * publicado sem alteração a salvar; o convite só volta ao levantamento sem compra começada
 * (`purchases_started`). Evento arquivado recusa toda mudança; apagar recusa evento com
 * compra (só arquivar) ou já sendo apagado. Nada mais trava depois de publicar: a mudança vale
 * para quem ainda vai responder ou reservar.
 */
export const SelfServiceConflictCodeSchema = z.enum([
  'premises_changed',
  'purchases_closed',
  'already_published',
  'purchases_started',
  'event_archived',
  'event_has_purchase',
  'event_deleting',
]);
export type SelfServiceConflictCode = z.infer<typeof SelfServiceConflictCodeSchema>;

/** Confirmação recusada: faltam premissas. */
export const SelfServiceIncompleteErrorSchema = z.object({
  code: z.literal('premises_incomplete'),
  missing: z.array(MicePremisesKeySchema).min(1),
  rules: z.array(MiceEventRuleSchema).optional(),
});
export type SelfServiceIncompleteError = z.infer<typeof SelfServiceIncompleteErrorSchema>;
